import { WORK_AS_USER_SESSION_LOCALSTORAGE_KEY } from '../../session/constants';

const BUFFER_TOKEN_EXPIRATION = 30_000;
export const INSTANCE_NAME_STORAGE_KEY = 'instance_name';
export const ROOT_INSTANCE_NAME_STORAGE_KEY = 'root_instance_name';
export const LAST_VALID_INSTANCE_NAME_STORAGE_KEY = 'last_valid_instance_name';
export const BACK_TO_STORAGE_KEY = 'back_to';

export interface OIDCAuthInfo {
  accessToken: string;
  instanceUid: string;
  expirationTime: number;
  instanceName?: string;
}

export const OIDC_AUTH_INFO_PREFIX = 'oidc-auth-info';

const INSTANCE_NAME_PATTERN = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/i;

export const isValidInstanceName = (instanceName: string): boolean => INSTANCE_NAME_PATTERN.test(instanceName);

export function loadInstanceName(takeWorkAsIntoConsideration = false): string | undefined {
  try {
    const currentInstanceName = localStorage.getItem(INSTANCE_NAME_STORAGE_KEY) ?? undefined;

    if (takeWorkAsIntoConsideration && localStorage.getItem(WORK_AS_USER_SESSION_LOCALSTORAGE_KEY)) {
      return loadRootInstanceName() ?? currentInstanceName;
    }

    return currentInstanceName;
  } catch {
    return undefined;
  }
}

export function loadRootInstanceName(): string | undefined {
  try {
    return localStorage.getItem(ROOT_INSTANCE_NAME_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function storeRootInstanceName(instanceName: string): void {
  try {
    localStorage.setItem(ROOT_INSTANCE_NAME_STORAGE_KEY, instanceName);
  } catch {
    return;
  }
}

export function clearRootInstanceName(): void {
  try {
    localStorage.removeItem(ROOT_INSTANCE_NAME_STORAGE_KEY);
  } catch {
    return;
  }
}

export function storeInstanceName(instanceName: string): void {
  try {
    localStorage.setItem(INSTANCE_NAME_STORAGE_KEY, instanceName);
  } catch {
    return;
  }
}

export function loadLastValidInstanceName(): string | undefined {
  try {
    return localStorage.getItem(LAST_VALID_INSTANCE_NAME_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function storeLastValidInstanceName(instanceName: string): void {
  try {
    localStorage.setItem(LAST_VALID_INSTANCE_NAME_STORAGE_KEY, instanceName);
  } catch {
    return;
  }
}

export function storeBackTo(backTo: string): void {
  try {
    localStorage.setItem(BACK_TO_STORAGE_KEY, backTo);
  } catch {
    return;
  }
}

export function consumeBackTo(): string | undefined {
  try {
    const backTo = localStorage.getItem(BACK_TO_STORAGE_KEY) ?? undefined;
    localStorage.removeItem(BACK_TO_STORAGE_KEY);

    return backTo;
  } catch {
    return undefined;
  }
}

export function clearInstanceInformation(): void {
  try {
    localStorage.removeItem(INSTANCE_NAME_STORAGE_KEY);
    localStorage.removeItem(LAST_VALID_INSTANCE_NAME_STORAGE_KEY);
    localStorage.removeItem(ROOT_INSTANCE_NAME_STORAGE_KEY);
  } catch {
    return;
  }
}

export function getOIDCAuthInfoStorageKey(authority: string, clientId: string): string {
  return `${OIDC_AUTH_INFO_PREFIX}:${authority}:${clientId}`;
}

export function getExpirationTime(expiresIn: number) {
  return Date.now() + expiresIn * 1000;
}

export function loadOIDCAuthInfo(authority: string, clientId: string): OIDCAuthInfo | null {
  try {
    const storageKey = getOIDCAuthInfoStorageKey(authority, clientId);
    const oidcAuthInfoString = localStorage.getItem(storageKey);

    if (!oidcAuthInfoString) {
      return null;
    }

    return JSON.parse(oidcAuthInfoString) as OIDCAuthInfo;
  } catch {
    return null;
  }
}

export function storeOIDCAuthInfo(authority: string, clientId: string, info: OIDCAuthInfo): void {
  try {
    const storageKey = getOIDCAuthInfoStorageKey(authority, clientId);
    localStorage.setItem(storageKey, JSON.stringify(info));
  } catch {
    return;
  }
}

export function isOIDCAuthInfoExpired(info: OIDCAuthInfo | null): info is null {
  if (!info) {
    return true;
  }

  const { expirationTime } = info;

  return Date.now() > expirationTime - BUFFER_TOKEN_EXPIRATION;
}
